import type { OptLogActionSnapshot, OptLogVolumeActions } from '~/shared/types/api';

import { formatGiBWithValueAndUnit, formatVolumeMetric } from './formatNumber';
import { wireTypeToOptLogDisplayType } from './optLogActionTypeMapping';

function isModifyOptLogAction(action: OptLogVolumeActions): boolean {
  return wireTypeToOptLogDisplayType(String(action.type ?? '')) === 'Modify';
}

/** Empty slot line when a metric is aligned but missing on this side (no visible placeholder). */
export const optLogSnapshotEmptySlotLine = '';

/** @deprecated Use `optLogSnapshotEmptySlotLine` and `isEmptyOptLogSlotLine`. */
export const optLogSnapshotMetricPlaceholder = optLogSnapshotEmptySlotLine;

export function isEmptyOptLogSlotLine(line: string): boolean {
  return line === optLogSnapshotEmptySlotLine;
}

export const optLogSnapshotMetricKinds = ['tier', 'size', 'iops', 'throughput'] as const;

export function shouldShowOptLogPerformanceTier(tier: number): boolean {
  return tier > 2;
}

export type OptLogSnapshotMetricKind = (typeof optLogSnapshotMetricKinds)[number];

function formatSizeLine(sizeGb: number): string {
  const { value, unit } = formatGiBWithValueAndUnit(sizeGb, 1);
  return unit ? `${value} ${unit}` : String(value);
}

export function hasOptLogActionSnapshotData(snapshot?: OptLogActionSnapshot): boolean {
  if (!snapshot) {
    return false;
  }
  return (
    snapshot.diskSizeGb != null ||
    snapshot.iops != null ||
    snapshot.throughput != null ||
    (snapshot.performanceTier != null &&
      shouldShowOptLogPerformanceTier(snapshot.performanceTier))
  );
}

export function formatOptLogSnapshotMetricLine(
  kind: OptLogSnapshotMetricKind,
  snapshot: OptLogActionSnapshot,
  fallbackSizeGb?: number
): string | null {
  switch (kind) {
    case 'size': {
      const sizeGb = snapshot.diskSizeGb ?? fallbackSizeGb;
      if (sizeGb == null || sizeGb <= 0) {
        return null;
      }
      return formatSizeLine(sizeGb);
    }
    case 'iops':
      if (snapshot.iops == null) {
        return null;
      }
      return `${formatVolumeMetric(snapshot.iops)} IOPS`;
    case 'throughput':
      if (snapshot.throughput == null) {
        return null;
      }
      return `${formatVolumeMetric(snapshot.throughput)} MBps`;
    case 'tier':
      if (
        snapshot.performanceTier == null ||
        !shouldShowOptLogPerformanceTier(snapshot.performanceTier)
      ) {
        return null;
      }
      return `Performance Tier ${snapshot.performanceTier}`;
    default:
      return null;
  }
}

function kindsFromSnapshot(snapshot: OptLogActionSnapshot): OptLogSnapshotMetricKind[] {
  const kinds: OptLogSnapshotMetricKind[] = [];
  if (snapshot.diskSizeGb != null && snapshot.diskSizeGb > 0) {
    kinds.push('size');
  }
  if (snapshot.iops != null) {
    kinds.push('iops');
  }
  if (snapshot.throughput != null) {
    kinds.push('throughput');
  }
  if (
    snapshot.performanceTier != null &&
    shouldShowOptLogPerformanceTier(snapshot.performanceTier)
  ) {
    kinds.push('tier');
  }
  return kinds;
}

export interface OptLogActionSideSource {
  snapshot?: OptLogActionSnapshot;
  legacySizeGb?: number;
}

export function resolveOptLogBeforeSideSource(action: OptLogVolumeActions): OptLogActionSideSource {
  if (hasOptLogActionSnapshotData(action.before)) {
    const lines = getOptLogActionSnapshotLines(action.before!);
    if (lines.length > 0) {
      return { snapshot: action.before };
    }
  }
  const sizeGb = action.originalDiskSizeGb ?? action.oldSize;
  if (sizeGb != null && sizeGb > 0) {
    return { legacySizeGb: sizeGb };
  }
  return {};
}

export function resolveOptLogAfterSideSource(action: OptLogVolumeActions): OptLogActionSideSource {
  if (hasOptLogActionSnapshotData(action.after)) {
    const lines = getOptLogActionSnapshotLines(action.after!);
    if (lines.length > 0) {
      return { snapshot: action.after };
    }
  }
  const sizeGb = action.newDiskSizeGb ?? action.newSize;
  if (sizeGb != null && sizeGb > 0) {
    return { legacySizeGb: sizeGb };
  }
  return {};
}

function kindsFromSideSource(source: OptLogActionSideSource): OptLogSnapshotMetricKind[] {
  if (source.snapshot) {
    return kindsFromSnapshot(source.snapshot);
  }
  if (source.legacySizeGb != null) {
    return ['size'];
  }
  return [];
}

function formatMetricLineForSide(
  kind: OptLogSnapshotMetricKind,
  source: OptLogActionSideSource
): string | null {
  if (source.snapshot) {
    return formatOptLogSnapshotMetricLine(kind, source.snapshot);
  }
  if (kind === 'size' && source.legacySizeGb != null) {
    return formatSizeLine(source.legacySizeGb);
  }
  return null;
}

export function getOptLogActionMetricKinds(action: OptLogVolumeActions): OptLogSnapshotMetricKind[] {
  const afterKinds = new Set(kindsFromSideSource(resolveOptLogAfterSideSource(action)));
  // Modify: SaaS sends the full before snapshot even for unchanged fields — only show
  // metrics that also appear on after (the changed subset).
  if (isModifyOptLogAction(action)) {
    return optLogSnapshotMetricKinds.filter((kind) => afterKinds.has(kind));
  }
  const beforeKinds = new Set(kindsFromSideSource(resolveOptLogBeforeSideSource(action)));
  return optLogSnapshotMetricKinds.filter((kind) => beforeKinds.has(kind) || afterKinds.has(kind));
}

function getOptLogActionSlotLinesForSide(
  action: OptLogVolumeActions,
  side: 'before' | 'after'
): string[] {
  const kinds = getOptLogActionMetricKinds(action);
  const source =
    side === 'before' ? resolveOptLogBeforeSideSource(action) : resolveOptLogAfterSideSource(action);

  return kinds.map((kind) => {
    const line = formatMetricLineForSide(kind, source);
    return line ?? optLogSnapshotEmptySlotLine;
  });
}

export function getOptLogActionBeforeSlotLines(action: OptLogVolumeActions): string[] {
  return getOptLogActionSlotLinesForSide(action, 'before');
}

export function getOptLogActionAfterSlotLines(action: OptLogVolumeActions): string[] {
  return getOptLogActionSlotLinesForSide(action, 'after');
}

export function getOptLogActionSnapshotLines(
  snapshot: OptLogActionSnapshot,
  fallbackSizeGb?: number
): string[] {
  const lines: string[] = [];
  for (const kind of optLogSnapshotMetricKinds) {
    const line = formatOptLogSnapshotMetricLine(kind, snapshot, fallbackSizeGb);
    if (line != null) {
      lines.push(line);
    }
  }
  return lines;
}

function legacyBeforeSizeLines(action: OptLogVolumeActions): string[] {
  const sizeGb = action.originalDiskSizeGb ?? action.oldSize;
  if (sizeGb != null && sizeGb > 0) {
    return [formatSizeLine(sizeGb)];
  }
  return [];
}

function legacyAfterSizeLines(action: OptLogVolumeActions): string[] {
  const sizeGb = action.newDiskSizeGb ?? action.newSize;
  if (sizeGb != null && sizeGb > 0) {
    return [formatSizeLine(sizeGb)];
  }
  return [];
}

export function getOptLogActionBeforeLines(action: OptLogVolumeActions): string[] {
  if (isModifyOptLogAction(action)) {
    return getOptLogActionBeforeSlotLines(action).filter((line) => !isEmptyOptLogSlotLine(line));
  }
  if (hasOptLogActionSnapshotData(action.before)) {
    const lines = getOptLogActionSnapshotLines(action.before!);
    if (lines.length > 0) {
      return lines;
    }
  }
  return legacyBeforeSizeLines(action);
}

export function getOptLogActionAfterLines(action: OptLogVolumeActions): string[] {
  if (hasOptLogActionSnapshotData(action.after)) {
    const lines = getOptLogActionSnapshotLines(action.after!);
    if (lines.length > 0) {
      return lines;
    }
  }
  return legacyAfterSizeLines(action);
}

export function formatOptLogActionBeforeAfter(action: OptLogVolumeActions): {
  before: string;
  after: string;
} {
  return {
    before: getOptLogActionBeforeLines(action).join('\n'),
    after: getOptLogActionAfterLines(action).join('\n'),
  };
}

export interface OptLogSnapshotDisplayRow {
  kind: OptLogSnapshotMetricKind;
  isPlaceholder: boolean;
  primaryValue?: string;
  primarySuffix?: string;
  secondaryText?: string;
}

export function formatOptLogSnapshotDisplayRowAsLine(row: OptLogSnapshotDisplayRow): string {
  if (row.isPlaceholder) {
    return optLogSnapshotEmptySlotLine;
  }
  const parts = [row.primaryValue, row.primarySuffix].filter(
    (part): part is string => part != null && part !== ''
  );
  return parts.join(' ');
}

function buildSizeDisplayRow(sizeGb: number): OptLogSnapshotDisplayRow {
  const { value, unit } = formatGiBWithValueAndUnit(sizeGb, 1);
  return {
    kind: 'size',
    isPlaceholder: false,
    primaryValue: String(value),
    primarySuffix: unit || undefined,
  };
}

function buildDisplayRowFromSnapshot(
  kind: OptLogSnapshotMetricKind,
  snapshot: OptLogActionSnapshot,
  legacySizeGb?: number
): OptLogSnapshotDisplayRow | null {
  switch (kind) {
    case 'size': {
      const sizeGb = snapshot.diskSizeGb ?? legacySizeGb;
      if (sizeGb == null || sizeGb <= 0) {
        return null;
      }
      return buildSizeDisplayRow(sizeGb);
    }
    case 'iops':
      if (snapshot.iops == null) {
        return null;
      }
      return {
        kind: 'iops',
        isPlaceholder: false,
        primaryValue: formatVolumeMetric(snapshot.iops),
        primarySuffix: 'IOPS',
      };
    case 'throughput':
      if (snapshot.throughput == null) {
        return null;
      }
      return {
        kind: 'throughput',
        isPlaceholder: false,
        primaryValue: formatVolumeMetric(snapshot.throughput),
        primarySuffix: 'MBps',
      };
    case 'tier':
      if (
        snapshot.performanceTier == null ||
        !shouldShowOptLogPerformanceTier(snapshot.performanceTier)
      ) {
        return null;
      }
      return {
        kind: 'tier',
        isPlaceholder: false,
        primaryValue: 'Performance',
        primarySuffix: `Tier ${snapshot.performanceTier}`,
      };
    default:
      return null;
  }
}

function buildDisplayRowForKind(
  kind: OptLogSnapshotMetricKind,
  source: OptLogActionSideSource
): OptLogSnapshotDisplayRow {
  if (source.snapshot) {
    const row = buildDisplayRowFromSnapshot(kind, source.snapshot);
    if (row) {
      return row;
    }
  }
  if (kind === 'size' && source.legacySizeGb != null) {
    return buildSizeDisplayRow(source.legacySizeGb);
  }
  return { kind, isPlaceholder: true };
}

function getOptLogActionDisplayRowsForSide(
  action: OptLogVolumeActions,
  side: 'before' | 'after'
): OptLogSnapshotDisplayRow[] {
  const kinds = getOptLogActionMetricKinds(action);
  const source =
    side === 'before' ? resolveOptLogBeforeSideSource(action) : resolveOptLogAfterSideSource(action);

  return kinds.map((kind) => buildDisplayRowForKind(kind, source));
}

export function getOptLogActionBeforeDisplayRows(
  action: OptLogVolumeActions
): OptLogSnapshotDisplayRow[] {
  return getOptLogActionDisplayRowsForSide(action, 'before');
}

export function getOptLogActionAfterDisplayRows(
  action: OptLogVolumeActions
): OptLogSnapshotDisplayRow[] {
  return getOptLogActionDisplayRowsForSide(action, 'after');
}

function sortSizeGbFromSideSource(source: OptLogActionSideSource): number | undefined {
  if (source.snapshot?.diskSizeGb != null && source.snapshot.diskSizeGb > 0) {
    return source.snapshot.diskSizeGb;
  }
  if (source.legacySizeGb != null && source.legacySizeGb > 0) {
    return source.legacySizeGb;
  }
  return undefined;
}

/** Numeric size for server/client sort on the Before column (`originalDiskSizeGb` field). */
export function getOptLogActionBeforeSortSizeGb(action: OptLogVolumeActions): number | undefined {
  return sortSizeGbFromSideSource(resolveOptLogBeforeSideSource(action));
}

/** Numeric size for server/client sort on the After column (`newDiskSizeGb` field). */
export function getOptLogActionAfterSortSizeGb(action: OptLogVolumeActions): number | undefined {
  return sortSizeGbFromSideSource(resolveOptLogAfterSideSource(action));
}

